/*
  iOS-style dot pagination for Dawn's mobile sliders — the product media gallery
  plus the featured-collection / collection-list / featured-blog / multicolumn
  peek sliders.

  Those sliders are plain <slider-component>s (global.js), whose update() only
  writes the "1 / 3" counter — dot handling lives in SlideshowComponent, which
  none of them use. Rather than swapping the custom element (which
  media-gallery.js depends on) this element derives its own state from the
  track's scroll position, so it never touches SliderComponent internals beyond
  reading the cached slide offset when it happens to be available.

  Attributes:
    data-media           media query that must match for dots to render.
                         Defaults to "(max-width: 749px)" — the width at which a
                         `.slider--mobile` track scrolls. Sections whose track is
                         `.slider--tablet` pass "(max-width: 989px)" instead.
    data-count           last-resort page count when the track can't be measured.
    data-label-template  aria-label per dot, "[index]" replaced by the 1-based page.
    data-live-region     id of a role="status" element to announce page changes into.
*/
if (!customElements.get('brand-slider-dots')) {
  customElements.define(
    'brand-slider-dots',
    class BrandSliderDots extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.sliderComponent = this.closest('slider-component');
        this.track = this.sliderComponent && this.sliderComponent.querySelector('[id^="Slider-"]');
        if (!this.track) return;

        this.liveRegion = this.dataset.liveRegion ? document.getElementById(this.dataset.liveRegion) : null;
        this.labelTemplate = this.dataset.labelTemplate || 'Slide [index]';
        this.media = window.matchMedia(this.dataset.media || '(max-width: 749px)');
        this.links = [];
        this.count = 0;
        this.activeIndex = 0;

        this.onScroll = this.onScroll.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        // Slide widths (and therefore the page count) depend on layout, so
        // re-sync whenever the track is resized — this also covers crossing the
        // breakpoint and variant changes that add or remove media.
        // The extra frame lets SliderComponent's own ResizeObserver recompute
        // its cached offset first — observer callback order between the two is
        // not guaranteed, and a rAF queued from inside one runs after both.
        this.onResize = () => {
          this.sync();
          requestAnimationFrame(() => this.sync());
        };

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.track);
        } else {
          window.addEventListener('resize', this.onResize);
        }

        // Belt and braces: a breakpoint can be crossed without the track's own
        // border box changing width.
        if (this.media.addEventListener) this.media.addEventListener('change', this.onResize);

        this.sync();
        requestAnimationFrame(() => this.sync());
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        else if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.media && this.media.removeEventListener && this.onResize) {
          this.media.removeEventListener('change', this.onResize);
        }
        clearTimeout(this.announceTimer);
        clearTimeout(this.resetTimer);
      }

      /* Distance between two consecutive slides. Prefers SliderComponent's
         cached value; falls back to measuring so the dots still work if that
         component bailed out during construction. */
      get itemOffset() {
        const cached = this.sliderComponent && this.sliderComponent.sliderItemOffset;
        if (cached) return cached;
        const slides = this.track.querySelectorAll('[id^="Slide-"], .slider__slide');
        if (slides.length < 2) return 0;
        return slides[1].offsetLeft - slides[0].offsetLeft;
      }

      /*
        Page count is measured from the scrollable extent rather than read off
        SliderComponent.totalPages.

        totalPages is `slides - floor(slidesPerPage) + 1`. The floor() is the
        problem: on a peek slider showing ~2.3 cards per viewport it counts a
        final page whose first slide can never reach the left edge, so that dot
        would be permanently dead — Dawn's own counter has the same off-by-one
        and tops out at "5 / 6". At this theme's grid spacing the two agree on a
        390-430px viewport, but the margin is a fraction of a card wide, so any
        spacing_grid_horizontal change would silently break it.

        round(maxScroll / offset) is consistent with scrollIndex by construction:
        the last dot is exactly the index reported once the track is scrolled to
        the end, whatever the peek geometry works out to.
      */
      get pageCount() {
        if (!this.media.matches) return 0;

        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        // Not scrollable at this width — e.g. a section whose grid only becomes
        // a slider on desktop, or one whose slides all fit.
        if (maxScroll <= 1) return 0;

        const offset = this.itemOffset;
        if (offset > 0) return Math.round(maxScroll / offset) + 1;

        const total = this.sliderComponent && this.sliderComponent.totalPages;
        if (typeof total === 'number' && total > 0) return total;
        const fallback = parseInt(this.dataset.count, 10);
        return Number.isNaN(fallback) ? 0 : fallback;
      }

      get scrollIndex() {
        const offset = this.itemOffset;
        if (!offset || this.count < 2) return 0;
        const index = Math.round(this.track.scrollLeft / offset);
        return Math.min(Math.max(index, 0), this.count - 1);
      }

      sync() {
        const count = this.pageCount;
        if (count !== this.count) this.render(count);
        if (this.count < 2) return;
        this.setActive(this.scrollIndex);
      }

      render(count) {
        this.count = count;
        this.links = [];
        this.innerHTML = '';
        if (count < 2) return;

        const fragment = document.createDocumentFragment();
        for (let i = 0; i < count; i++) {
          const link = document.createElement('button');
          link.type = 'button';
          link.className = 'brand-slider-dots__link';
          link.setAttribute('aria-label', this.labelTemplate.replace('[index]', i + 1));
          link.setAttribute('aria-current', 'false');
          link.appendChild(document.createElement('span')).className = 'brand-slider-dots__dot';
          link.addEventListener('click', this.onDotClick.bind(this, i));
          fragment.appendChild(link);
          this.links.push(link);
        }
        this.appendChild(fragment);
        this.activeIndex = -1;
      }

      setActive(index) {
        if (index === this.activeIndex) return;
        this.activeIndex = index;

        this.links.forEach((link, i) => {
          const isActive = i === index;
          link.classList.toggle('is-active', isActive);
          link.setAttribute('aria-current', isActive ? 'true' : 'false');

          // Shrinking edge dots, iOS UIPageControl style.
          const distance = Math.abs(i - index);
          link.classList.toggle('is-sm', this.count > 5 && distance === 3);
          link.classList.toggle('is-xs', this.count > 5 && distance >= 4);
        });
      }

      onScroll() {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          if (this.count < 2) return;
          const index = this.scrollIndex;
          if (index === this.activeIndex) return;
          this.setActive(index);
          this.announce(index);
        });
      }

      onDotClick(index) {
        const offset = this.itemOffset;
        if (!offset) return;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left: index * offset, behavior: reduceMotion ? 'auto' : 'smooth' });
        this.setActive(index);
        this.announce(index);
      }

      /* Only the product gallery passes a live region: it reuses the existing
         role="status" element, which media-gallery.js leaves aria-hidden between
         announcements, so unhide it the same way it does before writing.
         Elsewhere the dots' aria-label + aria-current carry the state, which is
         what Dawn's own slideshow dots expose. */
      announce(index) {
        if (!this.liveRegion) return;
        const template = window.accessibilityStrings && window.accessibilityStrings.imageAvailable;
        if (!template) return;

        clearTimeout(this.announceTimer);
        clearTimeout(this.resetTimer);
        this.announceTimer = setTimeout(() => {
          this.liveRegion.setAttribute('aria-hidden', 'false');
          this.liveRegion.textContent = template.replace('[index]', index + 1);
          this.resetTimer = setTimeout(() => this.liveRegion.setAttribute('aria-hidden', 'true'), 2000);
        }, 350);
      }
    }
  );
}
